import { Head, Link } from "@inertiajs/react"
import { ArrowLeft, Pencil } from "lucide-react"
import { useTranslation } from "react-i18next"

import { MasterclassCompletionsSection } from "@/components/masterclasses/masterclass-completions-section"
import { MasterclassDeleteConfirmDialog } from "@/components/masterclasses/masterclass-delete-confirm-dialog"
import { MasterclassModulesSection } from "@/components/masterclasses/masterclass-modules-section"
import { MasterclassPublishConfirmDialog } from "@/components/masterclasses/masterclass-publish-confirm-dialog"
import { MasterclassStatusBadge } from "@/components/masterclasses/masterclass-status-badge"
import { MasterclassUnpublishConfirmDialog } from "@/components/masterclasses/masterclass-unpublish-confirm-dialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import { editMasterclassPath, masterclassesPath } from "@/routes"
import type { MasterclassShowProps } from "@/types/masterclasses"

/**
 * OKS-54 — `Brand/Masterclasses/Show`.
 *
 * Read-only detail view + Edit / Publish (or Unpublish) / Delete
 * actions + non-functional Modules placeholder (PRD §1; OKS-55 wires
 * the real modules surface).
 *
 * The Publish/Unpublish/Delete buttons are AlertDialog-wrapped Inertia
 * `<Form>` POSTs/DELETEs; per-button visibility is gated by the
 * controller's `can_publish` / `can_unpublish` / `can_destroy` props.
 */
export default function MasterclassesShow({
  masterclass,
  completions,
  modules,
  module_count,
  can_create_module,
  cap_warning_threshold,
  cap_hard,
  can_edit,
  can_destroy,
  can_publish,
  can_unpublish,
}: MasterclassShowProps) {
  const { t, i18n } = useTranslation()

  const id = masterclass.id ?? 0
  const locale = i18n.language === "en" ? "en-US" : "fr-FR"
  const dateFormatter = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  })

  const publishedDate = masterclass.published_at
    ? dateFormatter.format(new Date(masterclass.published_at))
    : null

  // Inline call sites below — keep both keys statically grep-able for
  // the `inertia_i18n` health checker (the variable-key pattern hides
  // them from the static sweep).
  const publishedCopy =
    masterclass.state === "published"
      ? t("masterclasses.show.published_at", { date: publishedDate ?? "" })
      : t("masterclasses.show.previously_published_at", {
          date: publishedDate ?? "",
        })

  return (
    <AppLayout>
      <Head title={`${masterclass.title} — ${t("masterclasses.head_title")}`} />
      <div
        className="mx-auto flex w-full max-w-4xl flex-col gap-6 p-6"
        data-testid="masterclass-show-page"
      >
        <header className="flex flex-col gap-3">
          <Link
            href={masterclassesPath()}
            className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1 text-sm"
            data-testid="masterclass-show-back-link"
          >
            <ArrowLeft className="size-4" aria-hidden="true" />
            {t("masterclasses.show.breadcrumb_root")}
          </Link>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex flex-col gap-2">
              <h1
                className="text-foreground line-clamp-2 text-2xl font-semibold tracking-tight"
                data-testid="masterclass-show-title"
              >
                {masterclass.title}
              </h1>
              <div className="flex flex-wrap items-center gap-2">
                <MasterclassStatusBadge state={masterclass.state} />
                <span className="text-muted-foreground text-sm">
                  {masterclass.topic}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {can_edit && (
                <Button
                  asChild
                  variant="outline"
                  data-testid="masterclass-edit-button"
                >
                  <Link href={editMasterclassPath(id)}>
                    <Pencil className="mr-1 size-4" aria-hidden="true" />
                    {t("masterclasses.show.actions.edit")}
                  </Link>
                </Button>
              )}
              {can_publish && (
                <MasterclassPublishConfirmDialog masterclassId={id} />
              )}
              {can_unpublish && (
                <MasterclassUnpublishConfirmDialog masterclassId={id} />
              )}
              {can_destroy && (
                <MasterclassDeleteConfirmDialog masterclassId={id} />
              )}
            </div>
          </div>
        </header>

        {/* Overview card */}
        <Card data-testid="masterclass-show-overview">
          <CardHeader>
            <CardTitle>{masterclass.topic}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <p
              className="text-foreground text-sm whitespace-pre-wrap"
              data-testid="masterclass-show-description"
            >
              {masterclass.description}
            </p>
            {masterclass.created_by && (
              <p className="text-muted-foreground text-xs">
                {t("masterclasses.show.created_by", {
                  name: masterclass.created_by.name,
                })}
              </p>
            )}
            {publishedDate && (
              <p
                className="text-muted-foreground text-xs"
                data-testid="masterclass-show-published-at"
              >
                {publishedCopy}
              </p>
            )}
          </CardContent>
        </Card>

        {/* OKS-57 — Per-masterclass Completions card. Slotted between
            Overview and Modules placeholder per UX Spec §2.1. */}
        <MasterclassCompletionsSection
          completions={completions}
          masterclassState={masterclass.state}
          masterclassPublishedAt={masterclass.published_at}
        />

        {/* OKS-55 — Inline modules section (replaces the OKS-54
            "Coming soon" placeholder; UX Spec §2.1; TRD KD-9). */}
        <MasterclassModulesSection
          masterclassId={id}
          modules={modules}
          moduleCount={module_count}
          canCreate={can_create_module}
          capWarningThreshold={cap_warning_threshold}
          capHard={cap_hard}
        />
      </div>
    </AppLayout>
  )
}
